import { Box, Flex, createStyles } from '@mantine/core';
import { IconButton, Text, Title } from '@repo/foundations';
import type React from 'react';
import { SecodaFullTextLogo } from '../../SecodaFullTextLogo';

interface AuthLayoutBaseProps {
	title?: string;
	subTitle?: string;
	children: React.ReactNode;
	onClickBackButton?: () => void;
	backButtonTooltipText?: string;
}

const useStyles = createStyles((theme) => ({
	page: {
		display: 'flex',
		justifyContent: 'center',
		backgroundColor: theme.other.getColor('surface/tertiary/default'),
		minHeight: '100vh',
		alignItems: 'center',
	},
	logo: {
		position: 'absolute',
		top: theme.other.space[8],
		left: theme.other.space[8],
		zIndex: 1,
	},
	background: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		justifyContent: 'center',
		alignItems: 'center',
		width: '100vw',
		height: '100vh',
		display: 'flex',
		overflow: 'hidden',
	},
	backgroundImage: {
		filter: 'blur(150px)',
		borderRadius: '1280px',
		width: '150vw',
		maxWidth: 'none',
		height: '150vw',
		position: 'absolute',
		top: '50vh',
		transformStyle: 'preserve-3d',
		willChange: 'transform',
		animation: 'spin 15s infinite linear',
		transform:
			'translate3d(0px, 0px, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) skew(0deg, 0deg);',
	},
	contents: {
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		zIndex: 1,
		minWidth: theme.other.space[80],
	},
	backgroundPartAnimation: {
		transformOrigin: 'bottom center',
		// eslint-disable-next-line no-custom-animation/no-custom-animation
		animation: 'spin 10s infinite linear',
	},
}));

export function AuthLayoutBase({
	title,
	subTitle,
	children,
	onClickBackButton,
	backButtonTooltipText,
}: AuthLayoutBaseProps) {
	const { classes } = useStyles();

	return (
		<Box className={classes.page}>
			<Flex className={classes.logo} align="center" gap="sm">
				{onClickBackButton && (
					<IconButton
						iconName="chevronLeft"
						onClick={onClickBackButton}
						tooltip={backButtonTooltipText}
					/>
				)}
				<SecodaFullTextLogo />
			</Flex>
			<Box className={classes.contents}>
				{title && (
					<Title order={1} size="lg" ta="center">
						{title}
					</Title>
				)}
				{subTitle && (
					<Text size="sm" ta="center" color="text/secondary/default">
						{subTitle}
					</Text>
				)}
				{children}
			</Box>
			<Box className={classes.background}>
				<img
					src="https://secoda-public-media-assets.s3.us-east-1.amazonaws.com/688a031e2448425e432b73a5_gradient.webp"
					loading="lazy"
					alt=""
					className={classes.backgroundImage}
				/>
			</Box>
		</Box>
	);
}
